import { track } from "@/lib/analytics";
import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, Loader2, FileText } from "lucide-react";
import { API, GREEN, MUTE, INK, errMsg } from "./common";

/**
 * Drag-and-drop / click-to-pick for one or many bill PDFs. A PDF may hold a whole year of bills —
 * the server splits it. Calls onDone({bills, consumers}) with the server response.
 */
export default function UploadDrop({ consumerId, leadId, projectId, onDone, compact = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [queue, setQueue] = useState([]);

  const send = async (files) => {
    const pdfs = Array.from(files).filter((f) => /\.pdf$/i.test(f.name) || f.type === "application/pdf");
    if (!pdfs.length) { toast.error("Only PDF bills are supported"); return; }
    if (pdfs.length > 24) { toast.error("Upload up to 24 files at a time"); return; }
    const fd = new FormData();
    pdfs.forEach((f) => fd.append("files", f));
    if (consumerId) fd.append("consumer_id", consumerId);
    if (leadId) fd.append("lead_id", leadId);
    if (projectId) fd.append("project_id", projectId);
    setBusy(true); setQueue(pdfs.map((f) => f.name));
    try {
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      const n = data.bills?.length || 0;
      track("bill_audit_upload", { files: pdfs.length, bills: n, discom: data.bills?.[0]?.data?.discom || data.consumers?.[0]?.discom || undefined, state: data.consumers?.[0]?.state || undefined });
      track("generate_lead", { source: "bill_upload" });
      const low = (data.bills || []).filter((b) => (b.confidence ?? 0) < 0.9 || (b.warnings || []).length).length;
      toast.success(`${n} bill${n === 1 ? "" : "s"} read from ${pdfs.length} file${pdfs.length === 1 ? "" : "s"}${low ? ` — ${low} need${low === 1 ? "s" : ""} a quick review` : ""}`);
      (data.skipped || []).forEach((msg) => toast.warning(`Skipped — ${msg}`, { duration: 8000 }));
      onDone?.(data);
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setBusy(false); setQueue([]);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      data-testid="bill-upload-drop"
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) send(e.dataTransfer.files); }}
      onClick={() => !busy && inputRef.current?.click()}
      style={{
        border: `2px dashed ${over ? GREEN : "#CBD5E1"}`, background: over ? "#ECFDF5" : "#FAFCFB", borderRadius: 14, cursor: busy ? "wait" : "pointer",
        padding: compact ? "14px 16px" : "28px 20px", textAlign: "center", transition: "all .15s ease",
      }}
    >
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" multiple style={{ display: "none" }} onChange={(e) => send(e.target.files)} data-testid="bill-upload-input" />
      {busy ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <Loader2 size={compact ? 18 : 26} className="animate-spin" style={{ color: GREEN }} />
          <div style={{ fontSize: 12.5, fontWeight: 700, color: INK }}>Reading {queue.length} file{queue.length === 1 ? "" : "s"}…</div>
          <div style={{ fontSize: 11, color: MUTE }}>Scanned bills take longer — the pages are OCR'd and read by AI.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: compact ? "row" : "column", alignItems: "center", justifyContent: "center", gap: compact ? 10 : 8 }}>
          <UploadCloud size={compact ? 18 : 30} style={{ color: GREEN }} />
          <div>
            <div style={{ fontSize: compact ? 12.5 : 14, fontWeight: 800, color: INK }}>{compact ? "Add more bills" : "Drop electricity bills here"}</div>
            <div style={{ fontSize: 11.5, color: MUTE, marginTop: 2 }}>
              PDF only · many files at once · a single PDF with 12 months is fine · DHBVN / UHBVN · MSEDCL · UGVCL / DGVCL / MGVCL / PGVCL · Torrent
            </div>
          </div>
          {!compact && <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 6, padding: "7px 14px", borderRadius: 8, background: GREEN, color: "#fff", fontSize: 12, fontWeight: 700 }}><FileText size={13} />Choose PDFs</div>}
        </div>
      )}
    </div>
  );
}
